import './EngagementTables.scss';
import React, { useMemo, useState } from 'react';
import { FlightTable, FlightTextInput, getIcon } from '@flybits/design-system';
import DateRangePicker from 'components/Shared/DateRangePicker';
import type { Preset } from 'components/Shared/DateRangePicker';

const DATERANGE_PRESETS: Preset[] = ['today', 'last7', 'last30', 'last60', 'last90', 'custom'];
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AnalyticsExperiencesAPI from 'services/api/analytics-experiences.api';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import { CLASSES, DATE_FORMAT_OPTIONS } from '../constants';
import useDebounce from 'hooks/useDebounce';
import FilterByLabel from 'components/Analytics/FilterByLabel/FilterByLabel';
import { epochToDateTimeString } from 'helpers/common.helper';
import { ContentEngagementData, ENGAGEMENT_DATA_TYPES, LABEL_RULE } from 'components/Analytics/types';
import { useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import DownloadEngagementData from 'pages/Analytics/Overview/Engagement/DownloadEngagementData';
import useConfirmModal from 'hooks/useConfirmModal';
import { ConfirmationDialogProps } from 'components/Shared/shared.types';
import { ReactComponent as IconDownloadData } from 'assets/icons/icon-download-data.svg';
import { ReactComponent as IconContentEngagement } from 'assets/icons/content.svg';
import { AdvancedDateRange, DateRange } from 'pages/Analytics/types';
import AnalyticsErrorState from 'pages/Analytics/components/AnalyticsErrorState/AnalyticsErrorState';

const tableHeaders = [
  {
    name: 'key',
    key: 'key',
    isVisible: false,
    isSortable: false,
  },
  {
    name: 'Name',
    key: 'name',
    isVisible: true,
    isSortable: false,
  },
  {
    name: 'Type',
    key: 'type',
    isVisible: true,
    isSortable: false,
  },
  {
    name: 'Total views',
    key: 'total_views',
    isVisible: true,
    isSortable: true,
    tooltip: 'The total number of times this particular content item has been viewed.',
  },
  {
    name: 'Unique views',
    key: 'unique_views',
    isVisible: true,
    isSortable: true,
    tooltip: 'The number of unique users who have viewed this particular content item.',
  },
  {
    name: 'Unique engagement',
    key: 'unique_engagements',
    isVisible: true,
    isSortable: true,
    tooltip:
      'The number of unique users who have “engaged” a certain action. Engagement differs per content item type, depending on how your team has defined it per content item.',
  },
  {
    name: 'Unique engagement rate',
    key: 'unique_engagement_rate',
    isVisible: true,
    isSortable: true,
    tooltip: 'Unique engagement divided by unique views.',
  },
  {
    name: 'Unique fulfillment',
    key: 'unique_fulfillment',
    isVisible: true,
    isSortable: true,
    tooltip:
      'The number of unique users who have “fulfilled” a certain action. Fulfillment differs per content item type, depending on how your team has defined it per content item. ',
  },
  {
    name: 'Unique fulfillment rate',
    key: 'unique_fulfillment_rate',
    isVisible: true,
    isSortable: true,
    tooltip: 'Unique fulfillments divided by unique views.',
  },
];

const fetchContents = async (
  analyticsExperiencesApi: AnalyticsExperiencesAPI,
  page: number,
  perPage: number,
  dateRange: DateRange<Date>,
  search: string,
  labels: string,
  sortBy: string,
  sortOrder: string,
): Promise<ContentEngagementData> => {
  const res = await analyticsExperiencesApi.getContentsEngagement({
    lastFrom: Math.trunc((dateRange[0]?.getTime() ?? new Date().setHours(0, 0, 0, 0)) / 1000),
    lastTo: Math.trunc((dateRange[1]?.getTime() ?? new Date().getTime()) / 1000),
    offset: (page - 1) * perPage,
    limit: perPage,
    search,
    labelsFormula: labels ? `(${labels})` : undefined,
    sortBy,
    sortOrder,
  });

  return {
    contentsEngagement: res.data || [],
    pagination: res.pagination,
  };
};

const confirmationDialogProps: ConfirmationDialogProps = {
  icon: <IconDownloadData />,
  title: 'Download content table',
  description: 'Note that the table will be downloaded based on your current filters',
  primaryAction: {
    value: 'Download',
  },
  secondaryAction: {
    value: 'Cancel',
  },
};

const ContentEngagementTable = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const analyticsExperiencesApi = useMemo(() => new AnalyticsExperiencesAPI(), []);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  const [perPage, setPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState<AdvancedDateRange>([startOfToday, new Date(), 0, 'day']);
  const [queryDateRange, setQueryDateRange] = useState<AdvancedDateRange>([startOfToday, endOfDay]);
  const [search, setSearch] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [labelRule, setLabelRule] = useState<LABEL_RULE>();
  const labelString = labelRule === LABEL_RULE.CONTAINS_ALL_OF ? labels.join(',') : labels.join(';');
  const [sortByColumn, setSortByColumn] = useState('total_views');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const debouncedSearch = useDebounce(search, 300);
  const [contentDataForCSV, setContentDataForCSV] = useState<Record<string, string>[]>([]);
  const [isAllContentDataLoading, setAllContentDataLoading] = useState(false);
  const [DownloadConfirmModal, showDownloadConfirmModal] = useConfirmModal(confirmationDialogProps);

  const { data, status, isFetching, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'contents-engagement',
      currentPage,
      perPage,
      queryDateRange,
      debouncedSearch,
      labelString,
      sortByColumn,
      sortOrder,
    ],
    queryFn: () =>
      fetchContents(
        analyticsExperiencesApi,
        currentPage,
        perPage,
        [queryDateRange[0], queryDateRange[1]],
        debouncedSearch,
        labelString,
        sortByColumn,
        sortOrder,
      ),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleHeaderSort = (e: { key: string }) => {
    if (e.key !== sortByColumn) {
      setSortByColumn(e.key);
      setSortOrder('desc');
    } else {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    }
  };

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setCurrentPage(1);
    setSearch(value);
  };

  const handleLabelSelect = (selectedLabels: string[], labelRule: LABEL_RULE) => {
    setLabels(selectedLabels);
    setLabelRule(labelRule);
  };

  const handleDateRangeChange = (newDate: AdvancedDateRange) => {
    setDateRange(newDate);
    const hasFullRange = newDate[0] !== null && newDate[1] !== null;

    if (hasFullRange) {
      const endOfDay = new Date(newDate[1] ?? new Date());
      if (newDate.length < 3 || (newDate.length >= 3 && newDate[2] === 0)) {
        // Known interval - no custom or single day
        endOfDay.setHours(23, 59, 59);
      }

      setQueryDateRange([newDate[0], endOfDay]);
    }
  };

  const generateCSVRecords = (data: ContentEngagementData): Record<string, string>[] => {
    return data.contentsEngagement.map((content) => {
      return {
        'Content ID': content.contentId,
        'Content Name': content.name || content.contentId || '----',
        Type: content.templateName || '----',
        'Total Views': content.totalViews.toLocaleString(),
        'Unique Views': content.uniqueViews.toLocaleString(),
        'Unique Engagements': content.uniqueEngagements.toLocaleString(),
        'Unique Engagement Rate': `${(content.engagementRate * 100).toFixed(2)}%`,
        'Unique Fulfillment': content.uniqueFulfillments.toLocaleString(),
        'Unique Fulfillment Rate': `${(content.fulfillmentRate * 100).toFixed(2)}%`,
      };
    });
  };

  const handleClickDownload = async () => {
    try {
      if (await showDownloadConfirmModal()) {
        setAllContentDataLoading(true);
        const allData = await queryClient.fetchQuery({
          queryKey: ['all-contents-engagement', queryDateRange, debouncedSearch, labelString, sortByColumn, sortOrder],
          queryFn: () =>
            fetchContents(
              analyticsExperiencesApi,
              1,
              100000, // Sending higher limit to BE to fetch all records
              [queryDateRange[0], queryDateRange[1]],
              debouncedSearch,
              labelString,
              sortByColumn,
              sortOrder,
            ),
        });

        setContentDataForCSV(generateCSVRecords(allData));
      }
    } catch {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: {
          title: 'Error',
          content: 'Something went wrong and could not download content engagement data.',
          type: 'error',
        },
      });
    } finally {
      setAllContentDataLoading(false);
    }
  };

  return (
    <div className={CLASSES.CARD}>
      <div className={CLASSES.CARD_HEADER}>
        <h2>
          <IconContentEngagement className={CLASSES.HEADER_ICON} /> Content
        </h2>
        <div className={CLASSES.DATE_RANGE}>
          {(dateRange[2] !== undefined || dateRange[3] === 'allTime') &&
            (dateRange[2] === 0
              ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
              : `${epochToDateTimeString(dateRange[0] || 0, 'en-US', DATE_FORMAT_OPTIONS)} - ${epochToDateTimeString(
                  dateRange[1] || 0,
                  'en-US',
                  DATE_FORMAT_OPTIONS,
                )}`)}
        </div>
        <div>
          <DateRangePicker
            className={CLASSES.DATE_RANGE_DD}
            value={dateRange}
            onChange={handleDateRangeChange}
            maxDate={new Date()}
            presets={DATERANGE_PRESETS}
            ariaLabel="Select Content engagement date range"
          />
        </div>
        <FilterByLabel onLabelSelect={handleLabelSelect} />
        <div className={CLASSES.DOWNLOAD}>
          <DownloadEngagementData
            data={contentDataForCSV}
            type={ENGAGEMENT_DATA_TYPES.CONTENT}
            isDownloading={isAllContentDataLoading}
            onClickDownload={handleClickDownload}
          />
        </div>
      </div>
      {isLoading && !isError && (
        <div className={CLASSES.SPINNER}>
          <LoadingIcon />
        </div>
      )}
      <div className={CLASSES.SEARCH_WRAPPER}>
        <FlightTextInput
          onChange={handleSearchChange}
          value={search}
          placeholderText="Search"
          iconInput="search"
          width="280px"
          hasClearIcon
        />
      </div>
      {isError && (
        <AnalyticsErrorState compact onRetry={() => refetch()} isRetrying={isFetching} />
      )}
      {status === 'success' && (
        <>
          <div className={CLASSES.TABLE_WRAPPER}>
            {labels.length > 0 && (
              <div className={CLASSES.LABEL_LIST}>
                {'Labels: '}
                {labelRule === LABEL_RULE.CONTAINS_ALL_OF ? 'contains all of ' : 'contains any of '}
                {labels.map((label) => (
                  <span className={CLASSES.LABEL} key={label}>
                    {label}
                  </span>
                ))}
              </div>
            )}
            <FlightTable
              tableHeaders={tableHeaders}
              tableData={data.contentsEngagement.map((content) => {
                return {
                  key: content.contentId,
                  name: content.name || content.contentId || '----',
                  type: content.templateName || '----',
                  total_views: content.totalViews.toLocaleString(),
                  unique_views: content.uniqueViews.toLocaleString(),
                  unique_engagements: content.uniqueEngagements.toLocaleString(),
                  unique_engagement_rate: `${(content.engagementRate * 100).toFixed(2)}%`,
                  unique_fulfillment: content.uniqueFulfillments.toLocaleString(),
                  unique_fulfillment_rate: `${(content.fulfillmentRate * 100).toFixed(2)}%`,
                };
              })}
              sortByKey={sortByColumn}
              sortOrder={sortOrder}
              handleHeaderSort={handleHeaderSort}
              hasPaginationBeforeTable={false}
              hasPaginationAfterTable={!!data.contentsEngagement.length}
              paginationProps={{
                totalPageNumber: Math.ceil(data.pagination.totalRecords / perPage) || 1,
                currentPageNumber: currentPage,
                rowsPerPageOptions: [5, 10, 15, 20],
                currentRowsPerPage: perPage,
                handlePageChange: (page: number) => setCurrentPage(page),
                handleRowsPerPageChange: (num: number | string) => setPerPage(Number(num)),
              }}
              isLoading={isFetching}
            />
            {!!!data.contentsEngagement.length && !isFetching && (
              <div className={CLASSES.EMPTY_DATA}>
                {getIcon('search', {})}
                No results found
              </div>
            )}
          </div>
        </>
      )}
      <DownloadConfirmModal />
    </div>
  );
};

export default ContentEngagementTable;
